import 'core-js/actual/json/raw-json';
import * as React from 'react';
import * as ReactQuery from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { StatusCodes } from 'http-status-codes';
import {
  listAllScalesRequest,
  publishAIEyeDownlinkRequest,
} from '../apis/calyxSaaSDashboardAPI';
import { makeOption, validateArg } from '../utils/downlinkArgUtils';

/** @typedef {import('../utils/downlinkArgUtils').Arg} Arg */

// Note: some program on RPi requires some number fields to be float format
function enforceJsonFloat(value) {
  return Number.isInteger(value) ? JSON.rawJSON(value.toFixed(1)) : value;
}

/**
  @type {{
    name: string;
    args?: Arg[];
    payload: (args: Record<string, any>) => any;
  }[]}
**/
const availableDownlinks = [
  {
    name: 'Camera reboot',
    payload: (args) => ({
      "type": "system",
      "command": "system_reboot",
    }),
  },
  {
    name: 'System status report period',
    args: [
      {
        key: 'interval_sec',
        name: 'Interval (sec.)',
        type: 'integer',
        default: 300,
        validate: (value) => (value > 0 ? true : 'The interval must be positive!'),
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": "status_report_interval",
      "interval_sec": args.interval_sec,
    }),
  },
  {
    name: 'ToF calibration',
    args: [
      {
        key: 'offset_mm',
        name: 'Offset (mm)',
        type: 'number',
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": "tof_offset_setup",
      "offset_mm": enforceJsonFloat(args.offset_mm),
    }),
  },
  {
    name: 'Temperature and Humidity sensor',
    args: [
      {
        key: 'enable',
        name: 'Enable',
        type: 'option',
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": "temperature_humidity",
      "enable": args.enable,
    }),
  },
  {
    name: 'Laser-distance-meter(LDM) configuration',
    args: [
      {
        key: 'mode',
        name: 'Mode',
        type: 'option',
        halfWidth: true,
        options: [
          { name: 'Auto', value: 'auto' },
          { name: 'Enable', value: 'enable' },
          { name: 'Disable', value: 'disable' },
        ],
      },
      {
        key: 'ambient_light_threshold_percent',
        name: 'Ambient Light Threshold (%)',
        type: 'integer',
        halfWidth: true,
        validate: (value) => (value >= 0 && value <= 100 ? true : 'The threshold must be between 0 and 100!'),
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": "ldm_config",
      "mode": args.mode,
      "ambient_light_threshold_percent": args.ambient_light_threshold_percent,
    }),
  },
  {
    name: 'Laser-distance-meter(LDM) calibration',
    args: [
      {
        key: 'offset_mm',
        name: 'Offset (mm)',
        type: 'integer',
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": "ldm_offset_setup",
      "offset_mm": args.offset_mm,
    }),
  },
  {
    name: 'Inertial measurement unit(IMU) calibration',
    args: [
      {
        key: 'command',
        name: 'Command',
        type: 'option',
        options: [
          { name: 'Zero-point calibration', value: 'imu_set_zero' },
          { name: 'Clear the zero-point calibration setting', value: 'imu_clear_offset' },
        ],
      },
    ],
    payload: (args) => ({
      "type": "system",
      "command": args.command,
    }),
  },
  {
    name: 'Internet speed test',
    payload: (args) => ({
      "type": "system",
      "command": "internet_speed",
    }),
  },
  {
    name: 'Load-cell amplifier setup',
    args: [
      {
        key: 'command',
        name: 'Command',
        type: 'option',
        options: [
          { name: 'Load-cell amplifier enable', value: 'load_cell_enable' },
          { name: 'Load-cell amplifier disable', value: 'load_cell_disable' },
          { name: 'Power-on reset zero enable', value: 'power_on_reset_enable' },
          { name: 'Power-on reset zero disable', value: 'power_on_reset_disable' },
          { name: 'Read the status of the power-on reset function', value: 'power_on_reset_status' },
          { name: 'Clear display value', value: 'weight_clear' },
        ],
      },
    ],
    payload: (args) => ({
      "type": "loadcell_config",
      "command": args.command,
    }),
  },
  {
    name: 'Camera working modes and photo capture intervals',
    args: [
      {
        key: 'mode',
        name: 'Capture Mode',
        type: 'option',
        options: [
          { name: 'Left', value: 'LEFT' },
          { name: 'Stereo', value: 'STEREO' },
        ],
        optional: true,
      },
      {
        key: 'interval',
        name: 'Capture Interval (sec.)',
        type: 'number',
        validate: (value) => (value > 0 ? true : 'The interval must be positive!'),
        optional: true,
      },
      {
        key: 'birdDetectionBeforeUploadImg',
        name: 'Bird Detection Before Upload',
        type: 'option',
        options: [
          { name: 'Enable', value: true },
          { name: 'Disable', value: false },
        ],
        optional: true,
      },
    ],
    payload: (args) => ({
      "type": "photo_config",
      "command": {
        mode: args.mode,
        interval: enforceJsonFloat(args.interval),
        birdDetectionBeforeUploadImg: args.birdDetectionBeforeUploadImg,
      },
    }),
  },
];

export const ScaleDownlinkButton = (props) => {
  const allScalesQuery = ReactQuery.useQuery(
    ['list', 'scales'],
    () => listAllScalesRequest(),
  );

  const toast = React.useRef(
    /** @type {Toast | null} */ (null)
  );
  const [isDownlinkDialogVisible, setDownlinkDialogVisible] = React.useState(
    /** @type {boolean} */ (false)
  );
  const [selectedSerialNumbers, setSelectedSerialNumbers] = React.useState(
    /** @type {string[]} */ ([])
  );
  const [selectedDownlink, setSelectedDownlink] = React.useState(
    /** @type {typeof availableDownlinks[number] | null} */ (null)
  );
  const [selectedDownlinkArgs, setSelectedDownlinkArgs] = React.useState(
    /** @type {Record<string, any>} */ ({})
  );
  const [selectedDownlinkTimeout, setSelectedDownlinkTimeout] = React.useState(
    /** @type {number | undefined} */ (5000)
  );
  const [showResponseUplinkPayload, setShowResponseUplinkPayload] = React.useState(
    /** @type {boolean} */ (false)
  );

  // set default downlink args on downlink change
  React.useEffect(() => {
    setSelectedDownlinkArgs(
      Object.fromEntries((selectedDownlink?.args ?? [])
        .map((arg) => [arg.key, arg.default ?? undefined]))
    );
  }, [selectedDownlink]);

  const availableSerialNumbers = React.useMemo(
    () => /** @type {string[]} */ ((allScalesQuery.data ?? []).map((scale) => scale.serialNumber)),
    [allScalesQuery.data],
  );
  const availableSerialNumbersSet = React.useMemo(
    () => new Set(availableSerialNumbers),
    [availableSerialNumbers]
  );
  const isDownlinkValid = React.useMemo(
    () => {
      if (selectedDownlink == null) return false;
      if (selectedDownlinkTimeout == null) return false;
      if ((selectedDownlink.args ?? []).some((arg) => validateArg(arg, selectedDownlinkArgs[arg.key]) !== true)) return false;
      return true;
    },
    [selectedDownlink, selectedDownlinkArgs, selectedDownlinkTimeout],
  );
  const currentPayload = React.useMemo(
    () => isDownlinkValid ? selectedDownlink?.payload(selectedDownlinkArgs) : null,
    [isDownlinkValid, selectedDownlink, selectedDownlinkArgs],
  );

  async function sendDownlinkToDevice(/** @type {string} */ selectedSerialNumber) {
    try {
      const { topic: uplinkTopic, payload: uplinkPayload } = await publishAIEyeDownlinkRequest({
        serialNumber: selectedSerialNumber,
        payload: JSON.stringify(currentPayload),
        timeout: selectedDownlinkTimeout,
      });

      toast.current.show({
        severity: 'success',
        summary: `[${selectedSerialNumber}] Downlink Success`,
        detail: `The device ${selectedSerialNumber} successfully responded with a corresponding uplink.`,
      });

      if (showResponseUplinkPayload) {
        toast.current.show({
          severity: 'info',
          summary: `[${selectedSerialNumber}] Response Uplink`,
          detail: (
            <ul>
              <li>
                <b className="block">Topic</b>
                <code className="block py-1">{uplinkTopic}</code>
              </li>
              <li>
                <b className="block">Payload</b>
                <code className="block py-1">{uplinkPayload}</code>
              </li>
            </ul>
          ),
          sticky: true,
        });
      }
    } catch (err) {
      if (err?.response?.status === StatusCodes.REQUEST_TIMEOUT) {
        toast.current.show({
          severity: 'error',
          summary: `[${selectedSerialNumber}] Downlink Timeout`,
          detail: `The device ${selectedSerialNumber} didn't respond with a corresponding uplink within the timeout of ${selectedDownlinkTimeout}ms.`,
          sticky: true,
        });
      } else {
        toast.current.show({
          severity: 'error',
          summary: `[${selectedSerialNumber}] Unexpected Error`,
          detail: err.toString(),
          sticky: true,
        });
      }
    }
  }

  const sendDownlinksMutation = ReactQuery.useMutation({
    mutationFn: async () => {
      await Promise.allSettled(
        selectedSerialNumbers.map((selectedSerialNumber) => sendDownlinkToDevice(selectedSerialNumber)),
      );
    },
  });

  function editScaleListInCSV() {
    const csv = window.prompt('CSV:', selectedSerialNumbers.join(','));

    if (csv == null) return;

    const serialNumbers = csv.split(',')
      .map((e) => e.trim())
      .filter((e) => e !== '');

    setSelectedSerialNumbers(serialNumbers);
  }

  async function handleSendDownlinks() {
    if (!isDownlinkValid) return;

    sendDownlinksMutation.mutate();
  }

  return (
    <>
      <Toast ref={toast} />

      <Button
        label='Send Downlink to Scale'
        icon='pi pi-arrow-down'
        className='p-button-primary'
        onClick={() => setDownlinkDialogVisible(true)}
      />

      <Dialog
        visible={isDownlinkDialogVisible}
        header="Create Scale Downlink"
        footer={
          <div>
            <Button
              className="p-button-primary"
              label="Send Downlink"
              icon="pi pi-arrow-down"
              loading={sendDownlinksMutation.isLoading}
              disabled={selectedSerialNumbers.length === 0 || !isDownlinkValid}
              onClick={handleSendDownlinks}
            />
          </div>
        }
        style={{ width: '450px' }}
        blockScroll
        onHide={() => setDownlinkDialogVisible(false)}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-2">Serial Number(s)</label>
            <div className="flex gap-3">
              <MultiSelect
                value={selectedSerialNumbers}
                options={availableSerialNumbers}
                display="chip"
                filter
                showSelectAll={false}
                placeholder="Select Serial Number(s)"
                invalid={selectedSerialNumbers.length === 0}
                className="w-full"
                pt={{
                  label: {
                    style: {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                    },
                  },
                  token: ({ context }) => ({
                    style: {
                      margin: '0',
                      fontFamily: 'monospace',
                      color: !availableSerialNumbersSet.has(context.value) ? 'red' : undefined,
                    },
                  }),
                  item: {
                    style: {
                      fontFamily: 'monospace',
                    },
                  },
                }}
                onChange={(event) => setSelectedSerialNumbers(event.value.toSorted())}
              />
              <Button
                className="flex-none"
                icon="pi pi-align-left"
                outlined
                tooltip="Edit in CSV"
                onClick={editScaleListInCSV}
              />
            </div>
          </div>
          <div>
            <label className="block mb-2">Downlink Type</label>
            <Dropdown
              value={selectedDownlink}
              options={availableDownlinks}
              optionLabel="name"
              placeholder="Select a Downlink Type"
              invalid={selectedDownlink == null}
              className="w-full"
              onChange={(event) => setSelectedDownlink(event.value)}
            />
          </div>
          {(selectedDownlink?.args?.length ?? 0) > 0 ? (
            <div className="grid">
              {selectedDownlink?.args?.map((arg) => makeOption({
                arg,
                argValue: selectedDownlinkArgs[arg.key],
                setArgValue: (value) => {
                  setSelectedDownlinkArgs((prev) => ({ ...prev, [arg.key]: value }));
                },
              }))}
            </div>
          ) : null}
          <div>
            <label className="block font-medium text-lg mb-2">Payload Preview</label>
            <InputTextarea
              value={JSON.stringify(currentPayload, null, '\t')}
              rows={8}
              spellCheck={false}
              disabled
              className="w-full"
              style={{ fontFamily: 'monospace', tabSize: '4' }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label>Timeout (ms)</label>
            <InputNumber
              value={selectedDownlinkTimeout}
              useGrouping={false}
              maxFractionDigits={0}
              min={1}
              invalid={selectedDownlinkTimeout == null}
              onChange={(event) => setSelectedDownlinkTimeout(event.value ?? undefined)}
            />
          </div>
          <div>
            <label className="flex align-items-center gap-2">
              <Checkbox
                checked={showResponseUplinkPayload}
                onChange={(event) => setShowResponseUplinkPayload(event.checked ?? false)}
              />
              <span>Show response uplink</span>
            </label>
          </div>
        </div>
      </Dialog>
    </>
  );
};
